import { Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/layout/logo';
import { NewsletterForm } from '@/components/newsletter/newsletter-form';
import { Container } from '@/components/ui/primitives';
import { useSite } from '@/hooks/use-site';
import { paths } from '@/lib/paths';

/**
 * Pied de page.
 *
 * Deux détails alimentés par l'API plutôt que codés en dur :
 *
 * - l'adresse de contact vient de `site.contact_email` (`GET /site`) ;
 * - le pays affiché vient de `site.country` — le même front sert un site
 *   français, belge, portugais ou suisse sans modification.
 *
 * Les pages légales listées ici (mentions, conditions, confidentialité) ne
 * sont pas décoratives : le robot de vérification les visite lors de l'examen
 * de mise en production. Un site qui n'en dispose pas voit son examen échouer.
 */

type FooterLink = {
    to: string;
    label: string;
    /**
     * Repère du robot de mise en production. Les points d'entrée qu'il
     * cherche doivent être atteignables depuis l'accueil : le pied de page,
     * présent sur toutes les pages, est l'endroit naturel.
     */
    dashlite?: string;
};

const LINK_GROUPS: Array<{ title: string; links: FooterLink[] }> = [
    {
        title: 'Le service',
        links: [
            { to: paths.search, label: 'Rechercher une entreprise' },
            { to: paths.pricing, label: 'Tarif et accès' },
            { to: paths.account, label: 'Mon compte' },
            { to: paths.cancelSubscription, label: 'Résilier mon abonnement', dashlite: 'cancel-subscription' },
        ],
    },
    {
        title: 'Aide',
        links: [
            { to: paths.contact, label: 'Nous contacter', dashlite: 'contact' },
            { to: paths.forgotPassword, label: 'Mot de passe oublié', dashlite: 'password-reset' },
            { to: paths.newsletter, label: 'Newsletter' },
            { to: paths.unsubscribe, label: 'Se désinscrire' },
            { to: paths.integration, label: "Notes d'intégration" },
        ],
    },
    {
        title: 'Informations légales',
        links: [
            { to: paths.legal, label: 'Mentions légales' },
            { to: paths.terms, label: 'Conditions générales' },
            { to: paths.privacy, label: 'Confidentialité' },
        ],
    },
];

export function Footer() {
    const site = useSite();

    return (
        <footer className="border-line bg-surface-sunken mt-auto border-t">
            <Container className="py-14">
                <div className="grid gap-10 lg:grid-cols-[1.2fr_2fr]">
                    <div>
                        <Logo />

                        <p className="text-ink-muted mt-4 max-w-sm text-sm leading-relaxed">
                            DocInfoPro réunit les informations publiques d'une entreprise en une fiche claire :
                            identité, immatriculation, dirigeants, capital, activité et effectifs.
                        </p>

                        {site.contact_email && (
                            <a
                                href={`mailto:${site.contact_email}`}
                                className="text-brand-700 mt-4 inline-flex items-center gap-2 text-sm hover:underline"
                            >
                                <Mail className="size-4" aria-hidden />
                                {site.contact_email}
                            </a>
                        )}
                    </div>

                    <div className="grid gap-8 sm:grid-cols-3">
                        {LINK_GROUPS.map((group) => (
                            <nav key={group.title} aria-label={group.title}>
                                <h2 className="text-ink font-sans text-sm font-semibold">{group.title}</h2>
                                <ul className="mt-3 space-y-2">
                                    {group.links.map((link) => (
                                        <li key={link.to}>
                                            <Link
                                                data-dashlite={link.dashlite}
                                                to={link.to}
                                                className="text-ink-muted hover:text-brand-700 text-sm transition-colors"
                                            >
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        ))}
                    </div>
                </div>

                <div className="border-line mt-12 border-t pt-8">
                    <div className="grid gap-6 md:grid-cols-2 md:items-start">
                        <div>
                            <h2 className="text-ink font-sans text-sm font-semibold">Rester informé</h2>
                            <p className="text-ink-muted mt-1 text-sm">
                                Les évolutions du service, sans excès. Désinscription en un clic.
                            </p>
                        </div>
                        <NewsletterForm />
                    </div>
                </div>

                <p className="text-ink-muted border-line mt-10 border-t pt-6 text-xs">
                    © {new Date().getFullYear()} {site.name} — site de démonstration de l'API Dash Lite. Les données
                    d'entreprises affichées en environnement de test sont fictives.
                </p>
            </Container>
        </footer>
    );
}
